import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@workboard/wobo-ui';
import { ActivityIssueTypes } from '../../types/ai.types';
import { AiTypeIcon, AiTypeMenu } from '../aiType';

const getActivityTypeStorageKey = (workstreamId: string, columnId: string) => `workstream.${workstreamId}.column.${columnId}.defaultActivityType`;

function useDefaultActivityType(workstreamId: string, columnId: string) {
  const [type, setType] = useState<ActivityIssueTypes>(() => {
    const saved = localStorage.getItem(getActivityTypeStorageKey(workstreamId, columnId));
    return saved ? parseInt(saved, 10) : ActivityIssueTypes.ActionItem;
  });

  const setTypeAndSave = (newType: number) => {
    setType(newType);
    localStorage.setItem(getActivityTypeStorageKey(
      workstreamId,
      columnId
    ), newType.toString());
  };

  return [type, setTypeAndSave] as const;
}

interface AddActivityProps {
  onAdd: (type: number) => void
  workstreamId: string
  columnId: string
}

export default function AddActivity ({
  onAdd,
  workstreamId,
  columnId,
}: AddActivityProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [type, setType] = useDefaultActivityType(workstreamId, columnId);

  const handleButtonAddClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onAdd(type);
  };

  const handleButtonSetTypeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(buttonRef.current);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuTypeSelected = (newType: number) => {
    setAnchorEl(null);
    setType(newType);
  };

  return (
    <>
      <div className="add-activity">
        <button
          type="button"
          aria-label={t('Add Action')}
          className="add-activity-button"
          onClick={handleButtonAddClick}
        />
        <button
          ref={buttonRef}
          type="button"
          aria-label={t('Set Type')}
          className={`set-activity-type ${anchorEl ? 'active' : ''}`}
          onClick={handleButtonSetTypeClick}
        >
          <AiTypeIcon
            type={type}
            size={20}
          />
        </button>
      </div>
      <ThemeProvider>
        <AiTypeMenu
          anchorEl={anchorEl}
          hideTitle
          onClose={handleMenuClose}
          onTypeSelected={handleMenuTypeSelected}
        />
      </ThemeProvider>
    </>
  );
}
